import { yupResolver } from '@hookform/resolvers/yup';
import isNil from 'lodash/isNil';
import { useRef, useState } from 'react';
import type ReCAPTCHA from 'react-google-recaptcha';
import type { SubmitHandler } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { Accordion } from '@/components/Accordion';
import { Box } from '@/components/Box';
import { Button } from '@/components/Button';
import { FormProvider } from '@/components/FormProvider';
import { InputField } from '@/components/InputField';
import { Modal } from '@/components/Modal';
import { SectionHeader } from '@/components/SectionHeader';
import { Typography } from '@/components/Typography';
import { useGoogleAnalytics } from '@/hooks/useGoogleAnalytics';
import { useMyWatches } from '@/hooks/useMyWatches';
import type { ApiErrorWithType } from '@/lib/types';
import { commonMessages } from '@/messages/common';
import { myAccountMessages } from '@/messages/myAccount';
import { useRegionProvider } from '@/providers/RegionProvider';
import type { Watch } from '@/views/MyWatchesView';

import { ReCaptcha } from '../ReCaptcha';
import { ServerErrors } from '../ServerErrors';
import { Divider } from './components/Divider';
import { QrScanner } from './components/QrScanner';
import type { WatchSchema } from './schema';
import { watchSchema } from './schema';

import ReferenceHint from '@/assets/reference-and-serial-hint.png';

import * as M from './messages';
import * as S from './RegisterWatchModal.styles';

export interface RegisterWatchModalProps {
  isOpen?: boolean;
  onClose: () => void;
  setIsQrReaderOpen?: (type: boolean) => void;
  setSuccesses: (type: boolean) => void;
  setWatches: (watches: Watch[]) => void;
  watches: Watch[];
}

export interface RegisterWatchFormlProps {
  isOpen?: boolean;
  isQrReaderOpen?: boolean;
  onClose: () => void;
  setIsQrReaderOpen: (type: boolean) => void;
  setSuccesses: (type: boolean) => void;
  setWatches: (watches: Watch[]) => void;
  watches: Watch[];
}

export const RegisterWatchModal = ({
  isOpen,
  onClose,
  setSuccesses,
  setWatches,
  watches,
}: RegisterWatchModalProps) => {
  const [isQrReaderOpen, setIsQrReaderOpen] = useState(false);
  const { formatMessage } = useIntl();
  return (
    <S.Modal
      data-testid='RegisterWatchModal'
      gutters={false}
      isOpen={isOpen}
      maxWidth='xs'
      onClose={onClose}
    >
      <Modal.Header
        data-testid='RegisterWatchModalHeader'
        divider
        onClose={onClose}
      >
        <SectionHeader
          sizeDesktop={24}
          sizeMobile={24}
          text={formatMessage(myAccountMessages.accountWatchesRegisterCta)}
        />
      </Modal.Header>
      <S.ModalBody data-testid='RegisterWatchModalBody'>
        <S.InnerWrapper data-testid='RegisterWatchModalInnerWrapper'>
          {isQrReaderOpen ? (
            <QrScanner
              data-testid='RegisterWatchModalQrScanner'
              onClose={onClose}
              onQrReaderClose={() => setIsQrReaderOpen(false)}
              setSuccesses={setSuccesses}
              setWatches={setWatches}
              watches={watches}
            />
          ) : (
            <RegisterWatchForm
              data-testid='RegisterWatchModalRegisterWatchForm'
              onClose={onClose}
              setIsQrReaderOpen={setIsQrReaderOpen}
              setSuccesses={setSuccesses}
              setWatches={setWatches}
              watches={watches}
            ></RegisterWatchForm>
          )}
        </S.InnerWrapper>
      </S.ModalBody>
    </S.Modal>
  );
};

const RegisterWatchForm = ({
  onClose,
  setIsQrReaderOpen,
  setSuccesses,
  setWatches,
  watches,
}: RegisterWatchFormlProps) => {
  const [isVerifyingRecaptcha, setIsVerifyingRecaptcha] = useState(false);
  const reCaptchaRef = useRef<ReCAPTCHA>(null);
  const { formatMessage } = useIntl();
  const [isHintOpen, setIsHintOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const schema = watchSchema(formatMessage);
  const Provider = useRegionProvider();
  const {
    query: { languageCode },
  } = useRegionProvider();

  const [errors, setErrors] = useState<ApiErrorWithType[]>([]);
  const { addWatchToAccount, getWatchDetail } = useMyWatches();
  const formCtx = useForm({
    defaultValues: schema.getDefault(),
    resolver: yupResolver(schema),
  });
  const captchaValidation = formCtx.watch('captcha');
  const { sendRegisteredWatchEvent } = useGoogleAnalytics();
  const handleRegisterWatch: SubmitHandler<WatchSchema> = async ({
    model,
    sn,
  }) => {
    setIsProcessing(true);
    try {
      const res = await getWatchDetail(sn, model, Provider);
      const data = res.data;
      if (!isNil(data) && data.is_visible && !data.is_bind) {
        const subObj = {
          sn: sn,
          model: model,
          country: Provider?.country.code,
          channel: Provider?.query.channel,
          product_name: data.product_name,
        };
        const addWatch = await addWatchToAccount(subObj);
        if (addWatch.code === 200) {
          sendRegisteredWatchEvent(model);
          onClose();
          const arr = watches || [];
          arr?.push(data);
          setWatches(arr);
          setSuccesses(true);
          formCtx.reset();
        } else {
          throw new Error(res.code);
        }
      } else {
        throw new Error(res.code);
      }
    } catch (error: any) {
      setIsProcessing(false);
      formCtx.setValue('captcha', false);
      reCaptchaRef?.current?.reset();
      setErrors([
        {
          code: 'INVALID',
          field: error.message,
          type: 'addWatch',
        },
      ]);
    }
  };

  const handleOpenQrScanner = () => {
    if (!captchaValidation) {
      formCtx.setError('captcha', {
        type: 'manual',
        message: 'Captcha is required',
      });
    } else {
      setIsQrReaderOpen(true);
    }
  };
  return (
    <>
      <FormProvider
        name='test'
        {...formCtx}
        data-testid='RegisterWatchModalFormProvider'
        onSubmit={(data) => handleRegisterWatch(data)}
      >
        <S.Container data-testid='RegisterWatchModalContainer'>
          <ServerErrors
            data-testid='RegisterWatchModalServerErrors'
            errors={errors}
            type='warning'
          />
          <InputField
            data-testid='RegisterWatchModalModelInputField'
            label={formatMessage(commonMessages.reference)}
            name='model'
            required
          />
          <InputField
            data-testid='RegisterWatchModalSerialNumberInputField'
            label={formatMessage(commonMessages.serialNumber)}
            name='sn'
            required
          />
          <ReCaptcha
            ref={reCaptchaRef}
            data-testid='AccountBoxLoginFormReCaptcha'
            languageCode={languageCode}
            name='captcha'
            onChange={setIsVerifyingRecaptcha}
          />
          <Button
            data-testid='RegisterWatchModalSubmitButton'
            disabled={isProcessing || isVerifyingRecaptcha}
            isProcessing={isProcessing}
            type='submit'
          >
            {formatMessage(commonMessages.submit)}
          </Button>
          <Divider data-testid='RegisterWatchModalDivider' />
          <Button
            data-testid='RegisterWatchModalScanQrButton'
            onClick={handleOpenQrScanner}
          >
            {M.ScanQrCodeWarrantyWatch}
          </Button>
        </S.Container>
      </FormProvider>
      <S.AccordionContainer data-testid='RegisterWatchModalAccordionContainer'>
        <Accordion
          data-testid='RegisterWatchModalAccordion'
          isOpen={isHintOpen}
          onClick={() => setIsHintOpen(!isHintOpen)}
          title={
            <Typography>{formatMessage(M.messages.referenceHint)}</Typography>
          }
        >
          <Box>
            <S.Image
              alt='Reference and serial number hint'
              data-testid='RegisterWatchModalReferenceHintImage'
              src={ReferenceHint}
            />
          </Box>
        </Accordion>
      </S.AccordionContainer>
    </>
  );
};
RegisterWatchModal.RegisterWatchForm = RegisterWatchForm;
RegisterWatchModal.displayName = 'RegisterWatchModal';
